<template>
  <component
    :is="componentType"
    :target="target"
    v-bind="{ ...componentProps, ...Object.fromEntries(Object.entries($attrs).filter(([key]) => isNaN(key))) }"
    :class="[$attrs.class, componentProps.class]"
    v-track:click="gtmData"
    v-track:view="gtmData"
    @click="handleClick"
    :tabindex="componentType !== 'span' ? 0 : -1"
  >
    <slot>{{ text }}</slot>
  </component>
</template>

<script setup>
import { stripHtml } from '@/utils/common';

const NuxtLink = resolveComponent('NuxtLink');
const { $_gtm, $localeRootPath } = useNuxtApp();
const route = useRoute();

defineOptions({
  inheritAttrs: false,
});

const emit = defineEmits(['click']);

const props = defineProps({
  type: String,
  element: Object,
  text: String,
  target: String,
  url: String,
  activeClass: String,
  exactActiveClass: String,
  to: [Object, String],
  blockPosition: Number,
  index: Number,
  typeName: {
    type: String,
    default: 'LinkField',
  },
  trackingName: String,
  noTracking: Boolean,
});

const gtmData = computed(() => {
  if (props.noTracking) {
    return false;
  }

  const typeHandle = props.typeName;

  const promotionName =
    props.trackingName && typeHandle
      ? [typeHandle, stripHtml(props.trackingName)].join('_').toLowerCase()
      : stripHtml(props.trackingName ?? typeHandle ?? false);

  if (!promotionName) return false;

  const blockPosition = props.blockPosition ?? 0;

  return {
    promotion_id: promotionName.toLowerCase(),
    promotion_name: [route.name, promotionName].join(' / '),
    creative_name: typeHandle,
    creative_slot: [route.name, blockPosition, props.index].join(' / '),
  };
});

const handleClick = () => {
  if (props.element?.uri?.startsWith('exercises')) {
    const exerciseData = {
      event: 'gaEvent',
      event_name: 'exercise_view',
      exercise_view: {
        exercise_name: props.text,
        event_category: 'Exercise',
        event_action: 'View',
        event_label: props.text,
      },
    };
    $_gtm.push(exerciseData);
  }

  emit('click');
};

const componentType = computed(() => {
  if (props.target) return 'a';
  if (props.to) return NuxtLink;
  if (props.type?.includes('Entry') && props.element) return NuxtLink;
  if (props.type?.includes('Url') || props.type?.includes('Email')) return 'a';
  return 'span';
});

const componentProps = computed(() => {
  if (componentType.value === NuxtLink) {
    const to = props.to ?? $localeRootPath(props.element?.uri || '');

    // Simple path check using includes instead of startsWith
    const currentPath = route.path;

    // Don't match root path (/) or empty paths
    const linkIsActive = to !== '/' && to !== '' && currentPath.includes(to);

    return {
      to,
      activeClass: props.activeClass,
      exactActiveClass: props.exactActiveClass,
      class: linkIsActive ? props.activeClass : undefined,
    };
  }
  return {
    href: props.url || '',
  };
});
</script>
